import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PromotionTile from 'components/PromotionTile';
import ShopCard from 'components/ShopCard';
import styles from './ShopCardList.module.scss';

const INITIAL_TILE_LIMIT = 20;
const TILE_OBSERVED_INDEX = 9;
// used in the promo tiles Image to priority if above the fold
const PRIORITY_POSITION_LIMIT = 4;

export default function ShopCardList({
  backInStockText,
  badgeImages = [],
  closeoutText,
  defaultCategoryImages = [],
  featuredProductText,
  groupedDiscountsContent,
  packagedDateTooltipInfo,
  location = '',
  newProductText,
  pagePromotionTiles = [],
  placeholder,
  packagedDateConfiguration,
  priceDropText,
  products = [],
  terpeneTooltipInfo,
  withSideBarStyle,
}) {
  const { lineItems } = useSelector(({ cart }) => cart);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const productsLoading = useSelector((store) => store.productFetchStatus);
  const otherCategoryDefaultUrl = useMemo(
    () =>
      defaultCategoryImages.find(({ title }) => title === 'Other')?.image?.url,
    [defaultCategoryImages]
  );

  useEffect(() => {
    if (productsLoading !== loadingStatus) {
      if (productsLoading === 'LOADING') {
        setInitialLoad(true);
      }
      setLoadingStatus(productsLoading);
    }
  }, [productsLoading]);

  const promotionTilesArray = Object.values(pagePromotionTiles) || [];
  const productsLength = products.length;
  const tileList = promotionTilesArray
    .sort((a, b) => a.position - b.position)
    /* eslint-disable-next-line unicorn/no-array-reduce */
    .reduce((acc, tile) => {
      if (tile.position <= productsLength) {
        const clone = [...acc];
        clone.splice(tile.position - 1, 0, tile);
        return clone;
      }

      return acc;
    }, products);

  return (
    <ul
      className={classNames(
        'list-unstyled',
        `${withSideBarStyle ? styles.list_with_sidebar : styles.list}`
      )}
      data-value="category-page-list"
    >
      {(initialLoad ? tileList.slice(0, INITIAL_TILE_LIMIT) : tileList).map(
        (tile, ix) => {
          if (tile.categoryEffectStore) {
            const {
              background_image: backgroundImage,
              link,
              position,
              subtitle,
              text,
              title,
            } = tile;

            return (
              <PromotionTile
                key={`${title}${position}`}
                priority={position <= PRIORITY_POSITION_LIMIT}
                backgroundImage={backgroundImage?.url}
                handleObserved={
                  ix >= TILE_OBSERVED_INDEX && (() => setInitialLoad(false))
                }
                linkHref={link?.href}
                linkText={link?.title}
                subtitle={subtitle}
                text={text}
                title={title}
              />
            );
          }
          const {
            availableDiscounts,
            batchSKU,
            brand,
            category,
            classification,
            collection,
            discountedPrice,
            displayVariation,
            effect,
            images,
            inventoryStatus,
            mgTHC,
            mgCBD,
            packagedDate,
            percentTHC,
            percentCBD,
            name,
            price,
            SKU,
            tags,
            totalTerpenes,
            totalTerpenesUnit,
            variation,
            url,
          } = tile;

          const defaultImage =
            defaultCategoryImages.find(
              ({ title }) => title.toLowerCase() === category
            )?.image?.url || otherCategoryDefaultUrl;

          const lineItem = lineItems.find(
            (item) => item.batchSKU.split('~')[0] === batchSKU
          );

          return (
            <ShopCard
              key={`${name}${SKU}${batchSKU}${displayVariation}`}
              availableDiscounts={availableDiscounts}
              availableQuantity={lineItem?.availableQuantity ?? 0}
              backInStockText={backInStockText}
              badgeImages={badgeImages}
              batchSKU={batchSKU}
              brand={brand}
              cartQuantity={lineItem?.quantity ?? 0}
              category={category}
              closeoutText={closeoutText}
              currencyCode={price?.currencyCode}
              discountedCents={discountedPrice?.centAmount}
              displayTitle={name}
              displayVariation={displayVariation}
              effect={collection || effect}
              errorFallbackImage={defaultImage}
              featuredProductText={featuredProductText}
              groupedDiscountsContent={groupedDiscountsContent}
              inventoryStatus={inventoryStatus}
              packagedDateTooltipInfo={packagedDateTooltipInfo}
              mgCBD={mgCBD}
              mgTHC={mgTHC}
              newProductText={newProductText}
              packagedDate={packagedDate}
              packagedDateConfiguration={packagedDateConfiguration}
              packageSize={
                variation &&
                (variation.toLowerCase().includes('pack') ||
                  /\dx\d/i.test(variation))
                  ? Number.parseFloat(variation)
                  : variation
              }
              percentCBD={Math.round(1000 * percentCBD) / 1000}
              percentTHC={Math.round(1000 * percentTHC) / 1000}
              placeholder={placeholder}
              priceCents={price?.centAmount}
              priceDropText={priceDropText}
              primaryImageUrl={images?.[0]?.url ?? defaultImage}
              productCode={SKU}
              SKU={SKU}
              storeSelection={location}
              strain={classification}
              tags={tags}
              terpeneTooltipInfo={terpeneTooltipInfo}
              totalTerpenes={totalTerpenes}
              totalTerpenesUnit={totalTerpenesUnit}
              url={url}
              weight={variation ? Number.parseFloat(variation) : null}
              weightUOM={variation ? variation.replace(/[^A-Za-z]/g, '') : null}
              variation={variation}
              handleObserved={
                ix >= TILE_OBSERVED_INDEX && (() => setInitialLoad(false))
              }
            />
          );
        }
      )}
    </ul>
  );
}
